import { AuthenticationLogin } from '@adornis/authentication/client/defaults/AuthenticationLogin.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { promptDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { InvalidCredentialsError } from '@adornis/users/db/errors.js';
import type { BaseError } from '@adornis/validation/errors/BaseError.js';
import { html } from 'lit';
import { getMartinusAuthentication } from '../../authentication/martinus-authentication.js';
import { m } from '../../localization/out/messages.js';
import { UserEmailsAPI } from '../../user-emails/UserEmailsAPI.js';
import { displayMailError } from '../client-utils.js';

/** Asks for the account's mail/username and has the reset link sent there. */
const requestPasswordReset = async () => {
  const identifier = await promptDialog(
    html`
      <h2 class="font-semibold md:text-lg">${m._login_resetPasswordTitle()}</h2>
      <p>${m._login_resetPasswordHint()}</p>
    `,
    {
      placeholder: m._login_resetPasswordPlaceholder(),
      acceptText: m._login_resetPasswordSubmit(),
      declineText: m._login_cancel(),
    },
  ).catch(() => null);

  if (!identifier?.trim()) return;

  try {
    await UserEmailsAPI.sendPasswordResetMail(identifier.toLowerCase().trim())();
    void XSnackbar.show(m._login_resetPasswordSent());
  } catch (e) {
    displayMailError(e as BaseError);
  }
};

/**
 * Login accepts the email as well as the username. A shared email fails like a wrong password on
 * purpose: the server mails the inbox owner their usernames instead (see `sendAmbiguousAccountsMail`),
 * so the screen never reveals that an address belongs to several accounts.
 * Module-level so the login flow is not recreated on every render.
 */
const translateError = (error: Error) => {
  if (error instanceof InvalidCredentialsError) return m._login_errorInvalidCredentials();
  return m._login_error();
};

const microsoftDeniedMessage = (reasonCode: string): string | undefined =>
  reasonCode.startsWith('microsoft-') ? m.ambiguous_login() : undefined;

const footer = () => html`
  <button class="btn btn-ghost" type="button" @click=${() => void requestPasswordReset()}>
    ${m._login_resetPassword()}
  </button>
`;

const loginMethodOptions = () => {
  const microsoftMode = new URLSearchParams(location.search).get('login') === 'microsoft';
  return {
    enabledMethods: microsoftMode ? ['microsoft'] : ['password'],
    deniedMessage: microsoftDeniedMessage,
    footer: microsoftMode ? undefined : footer,
  };
};

/** The project login screen: the engine's login flow under the Martinus title. */
export const Login = stateful(
  (props: { onCompleted?: () => void }) => html`
    <div class="flex w-full flex-col items-center gap-6 p-8">
      <h1 class="text-center text-3xl font-bold">${m._login_welcome()}</h1>
      ${AuthenticationLogin({
        client: getMartinusAuthentication().client,
        ...loginMethodOptions(),
        intent: 'login',
        title: m._login_signIn(),
        translateError,
        onCompleted: props.onCompleted,
      })}
    </div>
  `,
);
